/**
 * Responsive Image System
 * Handles intelligent loading and optimization of responsive images
 * Supports lazy loading, intersection observer, and performance monitoring
 */

class ResponsiveImageSystem {
  constructor() {
    this.images = new Set();
    this.observer = null;
    this.loadedImages = new Set();
    this.performanceMetrics = {
      totalImages: 0,
      loadedImages: 0,
      failedImages: 0,
      averageLoadTime: 0
    };
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    this.setupIntersectionObserver();
    this.findAndSetupImages();
    this.handleResponsiveChanges();
    this.monitorPerformance();
  }

  setupIntersectionObserver() {
    // Only setup if browser supports it
    if (!('IntersectionObserver' in window)) {
      this.loadAllImages();
      return;
    }

    const options = {
      root: null,
      rootMargin: '50px', // Start loading 50px before entering viewport
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.loadImage(entry.target);
          this.observer.unobserve(entry.target);
        }
      });
    }, options);
  }

  findAndSetupImages() {
    const imageWrappers = document.querySelectorAll('[data-responsive-image]');
    
    imageWrappers.forEach(wrapper => {
      const images = wrapper.querySelectorAll('.responsive-image');
      
      images.forEach(img => {
        this.images.add(img);
        this.performanceMetrics.totalImages++;
        
        // Setup lazy loading
        if (img.loading === 'lazy') {
          this.setupLazyLoading(img);
        } else {
          this.loadImage(img);
        }
        
        // Add error handling
        img.addEventListener('error', () => this.handleImageError(img));
        img.addEventListener('load', () => this.handleImageLoad(img));
      });
    });
  }

  setupLazyLoading(img) {
    if (this.observer) {
      this.observer.observe(img);
    } else {
      // Fallback for browsers without intersection observer
      this.loadImage(img);
    }
  }

  loadImage(img) {
    if (this.loadedImages.has(img)) return;

    const startTime = performance.now();
    
    // Create a new image to preload
    const preloadImg = new Image();
    
    preloadImg.onload = () => {
      const loadTime = performance.now() - startTime;
      this.handleImageLoad(img, loadTime);
    };
    
    preloadImg.onerror = () => {
      this.handleImageError(img);
    };

    // Start loading
    preloadImg.src = img.src;
    if (img.srcset) {
      preloadImg.srcset = img.srcset;
    }
    
    this.loadedImages.add(img);
  }

  handleImageLoad(img, loadTime = 0) {
    img.classList.add('loaded');
    this.performanceMetrics.loadedImages++;
    
    if (loadTime > 0) {
      // Update average load time
      const totalTime = this.performanceMetrics.averageLoadTime * (this.performanceMetrics.loadedImages - 1) + loadTime;
      this.performanceMetrics.averageLoadTime = totalTime / this.performanceMetrics.loadedImages;
    }

    // Trigger custom event
    img.dispatchEvent(new CustomEvent('responsive-image:loaded', {
      detail: { img, loadTime }
    }));
  }

  handleImageError(img) {
    console.warn('Failed to load responsive image:', img.src);
    this.performanceMetrics.failedImages++;
    
    // Try to fallback to a different image source if available
    this.tryImageFallback(img);
    
    // Trigger custom event
    img.dispatchEvent(new CustomEvent('responsive-image:error', {
      detail: { img }
    }));
  }

  tryImageFallback(img) {
    const wrapper = img.closest('[data-responsive-image]');
    if (!wrapper) return;

    const imageType = img.dataset.imageType;
    let fallbackImg;

    if (imageType === 'mobile') {
      // Try to fallback to desktop image
      fallbackImg = wrapper.querySelector('.responsive-image--desktop');
    } else if (imageType === 'desktop') {
      // Try to fallback to mobile image
      fallbackImg = wrapper.querySelector('.responsive-image--mobile');
    }

    if (fallbackImg && fallbackImg.src) {
      img.src = fallbackImg.src;
      if (fallbackImg.srcset) {
        img.srcset = fallbackImg.srcset;
      }
    }
  }

  handleResponsiveChanges() {
    let resizeTimer;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.optimizeCurrentImages();
      }, 250);
    });

    // Handle orientation change on mobile
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.optimizeCurrentImages();
      }, 500);
    });
  }

  optimizeCurrentImages() {
    const currentBreakpoint = this.getCurrentBreakpoint();
    
    this.images.forEach(img => {
      if (!this.isImageVisible(img)) return;
      
      const wrapper = img.closest('[data-responsive-image]');
      const imageType = img.dataset.imageType;
      
      // Hide/show appropriate images based on current breakpoint
      if (currentBreakpoint === 'mobile') {
        if (imageType === 'desktop') {
          img.style.display = 'none';
        } else if (imageType === 'mobile') {
          img.style.display = 'block';
        }
      } else {
        if (imageType === 'mobile') {
          img.style.display = 'none';
        } else if (imageType === 'desktop') {
          img.style.display = 'block';
        }
      }
    });
  }

  getCurrentBreakpoint() {
    return window.innerWidth <= 749 ? 'mobile' : 'desktop';
  }

  isImageVisible(img) {
    const rect = img.getBoundingClientRect();
    return (
      rect.top < window.innerHeight &&
      rect.bottom > 0 &&
      rect.left < window.innerWidth &&
      rect.right > 0
    );
  }

  loadAllImages() {
    // Fallback method for browsers without intersection observer
    this.images.forEach(img => {
      this.loadImage(img);
    });
  }

  monitorPerformance() {
    // Report performance metrics after all images are processed
    setTimeout(() => {
      this.reportPerformance();
    }, 5000);
  }

  reportPerformance() {
    const metrics = this.performanceMetrics;
    
    if (window.console && window.console.group) {
      console.group('📊 Responsive Image Performance');
      console.log(`Total images: ${metrics.totalImages}`);
      console.log(`Loaded images: ${metrics.loadedImages}`);
      console.log(`Failed images: ${metrics.failedImages}`);
      console.log(`Success rate: ${((metrics.loadedImages / metrics.totalImages) * 100).toFixed(1)}%`);
      console.log(`Average load time: ${metrics.averageLoadTime.toFixed(2)}ms`);
      console.groupEnd();
    }

    // Trigger performance event for analytics
    window.dispatchEvent(new CustomEvent('responsive-image:performance', {
      detail: metrics
    }));
  }

  // Public API methods
  refresh() {
    this.findAndSetupImages();
  }

  getMetrics() {
    return { ...this.performanceMetrics };
  }

  preloadImage(wrapper) {
    const images = wrapper.querySelectorAll('.responsive-image');
    images.forEach(img => this.loadImage(img));
  }
}

// Initialize the system
const responsiveImageSystem = new ResponsiveImageSystem();

// Export for external use
window.ResponsiveImageSystem = ResponsiveImageSystem;
window.responsiveImageSystem = responsiveImageSystem;

// jQuery compatibility if needed
if (window.jQuery) {
  window.jQuery.fn.responsiveImage = function() {
    return this.each(function() {
      responsiveImageSystem.preloadImage(this);
    });
  };
}

// Event listeners for dynamic content
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => responsiveImageSystem.refresh(), 100);
});

document.addEventListener('shopify:section:reorder', () => {
  setTimeout(() => responsiveImageSystem.refresh(), 100);
});

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ResponsiveImageSystem;
}